import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  Bot, 
  Play, 
  Pause, 
  Trash2, 
  Edit, 
  Loader2, 
  AlertCircle, 
  RotateCcw
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { deleteAllFlows, activateFlow, deactivateFlow, resetAllConversations } from '../lib/flows';

interface ChatbotFlow {
  id: string;
  name: string;
  is_active: boolean;
  created_at: string;
  configuration: any;
}

export default function ChatbotFlows() {
  const { instanceId } = useParams();
  const navigate = useNavigate();
  const [flows, setFlows] = useState<ChatbotFlow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [updatingFlow, setUpdatingFlow] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchFlows();
  }, [instanceId]);

  async function fetchFlows() {
    try {
      if (!instanceId) {
        setError('ID de instancia no encontrado');
        setLoading(false);
        return;
      }

      const { data, error: fetchError } = await supabase
        .from('chatbots')
        .select('*')
        .eq('instance_id', instanceId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setFlows(data || []);
    } catch (err: any) {
      console.error('Error fetching flows:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteAllFlows() {
    if (!confirm('¿Estás seguro de que deseas eliminar todos los flujos? Esta acción no se puede deshacer.')) {
      return;
    }

    if (!instanceId) {
      setError('ID de instancia no encontrado');
      return;
    }

    try {
      setDeleting(true);
      setError('');
      await deleteAllFlows(instanceId);
      await fetchFlows();
    } catch (err: any) {
      console.error('Error deleting all flows:', err);
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  }

  async function deleteFlow(flowId: string) {
    if (!confirm('¿Estás seguro de que deseas eliminar este flujo?')) return;

    try {
      const { error: deleteError } = await supabase
        .from('chatbots')
        .delete()
        .eq('id', flowId);

      if (deleteError) throw deleteError;
      await fetchFlows();
    } catch (err: any) {
      console.error('Error deleting flow:', err);
      setError(err.message);
    }
  }

  async function handleToggleActive(flowId: string, isActive: boolean) {
    if (!instanceId) {
      setError('ID de instancia no encontrado');
      return;
    }

    try {
      setUpdatingFlow(flowId);
      setError('');

      if (isActive) {
        await deactivateFlow(instanceId);
      } else {
        await activateFlow(instanceId);
      }

      await fetchFlows();
    } catch (err: any) {
      console.error('Error toggling flow status:', err);
      setError(err.message);
    } finally {
      setUpdatingFlow(null);
    }
  }

  async function handleResetAllConversations() {
    if (!instanceId) {
      setError('ID de instancia no encontrado');
      return;
    }

    if (!confirm('¿Estás seguro de que deseas reiniciar todas las conversaciones? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      setResetting(true);
      setError('');
      await resetAllConversations(instanceId);
    } catch (err: any) {
      console.error('Error resetting conversations:', err);
      setError(err.message);
    } finally {
      setResetting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/channels')}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Volver a Canales
            </button>
          </div>
          <div className="flex items-center space-x-4">
            {flows.length > 0 && (
              <>
                <button
                  onClick={handleResetAllConversations}
                  disabled={resetting}
                  className="flex items-center px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50"
                >
                  {resetting ? (
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  ) : (
                    <RotateCcw className="h-5 w-5 mr-2" />
                  )}
                  Reiniciar Conversaciones
                </button>
                <button
                  onClick={handleDeleteAllFlows}
                  disabled={deleting}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting ? (
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="h-5 w-5 mr-2" />
                  )}
                  Eliminar todos los flujos
                </button>
              </>
            )}
            {flows.length === 0 && (
              <button
                onClick={() => navigate(`/chatbot/${instanceId}/editor/new`)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Plus className="h-5 w-5 mr-2" />
                Nuevo Flujo
              </button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <Bot className="h-5 w-5 text-gray-500 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">
                Flujos de Chatbot
              </h2>
            </div>
          </div>

          {error && (
            <div className="px-6 py-4">
              <div className="bg-red-50 border-l-4 border-red-400 p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha de Creación
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                      Cargando flujos...
                    </td>
                  </tr>
                ) : flows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                      No hay flujos para mostrar
                    </td>
                  </tr>
                ) : (
                  flows.map((flow) => (
                    <tr key={flow.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {flow.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(flow.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            flow.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {flow.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleToggleActive(flow.id, flow.is_active)}
                            disabled={updatingFlow === flow.id}
                            className={`p-1 text-white rounded ${
                              flow.is_active
                                ? 'bg-orange-500 hover:bg-orange-600'
                                : 'bg-green-500 hover:bg-green-600'
                            }`}
                            title={flow.is_active ? 'Desactivar' : 'Activar'}
                          >
                            {updatingFlow === flow.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : flow.is_active ? (
                              <Pause className="h-4 w-4" />
                            ) : (
                              <Play className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={() => navigate(`/chatbot/${instanceId}/editor/${flow.id}`)}
                            className="p-1 text-blue-600 hover:text-blue-900"
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => deleteFlow(flow.id)}
                            className="p-1 text-red-600 hover:text-red-900"
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}